'use client';

import { addToShortlist, getShortlist } from '@tac/shortlist';
import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState, useTransition } from 'react';

type CompareContextType = {
  compareVehicleIds: string[];
  isAddingVehicle: boolean;
  addVehicle: (vehicleId: string) => void;
};

const CompareContext = createContext<CompareContextType | undefined>(undefined);

export function CompareProvider({ children }: { children: ReactNode }) {
  const [compareVehicleIds, setCompareVehicleIds] = useState<string[]>([]);
  const [isAddingVehicle, startTransition] = useTransition();
  const router = useRouter();

  useEffect(() => {
    const update = () => setCompareVehicleIds(getShortlist());

    update();

    window.addEventListener('storage', update);
    return () => window.removeEventListener('storage', update);
  }, []);

  const addVehicle = (vehicleId: string) => {
    addToShortlist(vehicleId);
    const ids = getShortlist();
    startTransition(() => {
      router.push(`/v2/compare?vehicles=${ids.join(',')}`);
    });
  };

  return (
    <CompareContext.Provider value={{ compareVehicleIds, isAddingVehicle, addVehicle }}>
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const context = useContext(CompareContext);
  if (context === undefined) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
}
